import React, { useState, useMemo, useCallback, useEffect } from "react";
import { createWorkbookFromTable } from "../../utils/table_export";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  HStack,
  useToast,
  Collapse
} from "@chakra-ui/react";
import { useStores } from "../../store/store_context";
import ColumnFilters from "./column_filters";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
const now = dayjs();

const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
];

const MANAGER_ROLES = [
  "менеджер по продажам",
  "менеджер по продажам (удаленно)"
]

const isManagerRole = (role) =>
  MANAGER_ROLES.some(managerRole => role?.toLowerCase().includes(managerRole))

const TableData = observer(({ data, renderButtons }) => {
  const toast = useToast();
  const { pageStore } = useStores();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc',
    level: 'pivot'
  });
  const [totals, setTotals] = useState([]);
  let startDate = pageStore.start_date_month || pageStore.start_date_year;
  let endDate = pageStore.end_date_month || pageStore.end_date_year;

  const availableColumns = useMemo(() => {
    const filtered = pageStore.columnConfigStaff.filter(
      col => data.length > 0 && data[0].hasOwnProperty(col.key) && col.active
    );

    return filtered.map(col => ({
      ...col,
    }));
  }, [data, pageStore.columnConfigStaff]);

  const handleSort = useCallback((key, level) => {
    setSortConfig(prev => {
      if (prev.key === key && prev.level === level) {
        return {
          key,
          level,
          direction: prev.direction === "asc" ? "desc" : "asc"
        };
      }
      return { key, level, direction: "desc" };
    });
  }, []);

  const formatCellValue = useCallback((value, isNumeric = false) => {
    if (value == null) return "";

    if (typeof value === "number" && isNumeric) {
      return Math.round(value).toLocaleString("ru-RU");
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }

    return value || "";
  }, []);

  const formatDateKey = useCallback((key) => {
    if (key === "Все периоды") return key;

    if (pageStore.year_or_mouth_view === "month") {
      const [year, month] = key.split("-");
      return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
    }

    return `${key}`;
  }, [pageStore.year_or_mouth_view]);

  function getPeriods(start, end, mode) {
    const periods = [];
    let cursor = dayjs(start).startOf(mode);

    while (cursor.isBefore(end) || cursor.isSame(end, mode)) {
      periods.push({
        key: cursor.format(mode === "month" ? "YYYY-MM" : "YYYY"),
        start: cursor.startOf(mode),
        end: cursor.endOf(mode),
      });
      cursor = cursor.add(1, mode);
    }

    return periods;
  }

  function isInPeriod(item, period) {
    const employment = dayjs(item.employment);
    const dismissal = item.dismissal_date
      ? dayjs(item.dismissal_date)
      : null;

    return (
      employment.isSameOrBefore(period.end) &&
      (!dismissal || dismissal.isSameOrAfter(period.start))
    );
  }

  function calculateTotals(data, start, end, mode) {
    const periods = getPeriods(start, end, mode);

    return periods.map(period => {
      const sales = data.filter(
        i =>
          i.group === "Отдел продаж" &&
          isInPeriod(i, period) &&
          isManagerRole(i.role)
      ).length;

      const purchase = data.filter(
        i =>
          i.group === "Отдел закупок" &&
          isInPeriod(i, period)
      ).length;

      return {
        header: period.key,
        sales,
        purchase,
        ratio: purchase === 0 ? null : (sales / purchase).toFixed(2),
      };
    });
  }

  const { groupedData, allTimeKeys, totalSums, totalGlobalSum } = useMemo(() => {

    if (!pageStore.selected_view || !pageStore.year_or_mouth_view) {
      return { groupedData: null, allTimeKeys: [], totalSums: {}, totalGlobalSum: 0 };
    }

    const grouped = {};

    data.forEach(item => {
      const primaryKey = item.group;
      const secondaryKey = item.departament;

      if (!grouped[primaryKey]) {
        grouped[primaryKey] = {
          subGroups: {},
        };
      }

      if (!grouped[primaryKey].subGroups[secondaryKey]) {
        grouped[primaryKey].subGroups[secondaryKey] = {
          items: [],
        };
      }

      const subGroup = grouped[primaryKey].subGroups[secondaryKey];
      if (item.active) subGroup.items.push(item);
    });


    const groupedArray = Object.entries(grouped).map(([primaryKey, group]) => {
      const subGroups = Object.entries(group.subGroups).map(([secondaryKey, subGroup]) => ({
        secondaryKey,
        ...subGroup
      }));

      const hasOnlyDefaultSubgroup = subGroups.length === 1 && subGroups[0].secondaryKey === "Без подгруппы";

      return {
        primaryKey,
        subGroups: hasOnlyDefaultSubgroup
          ? [{ ...subGroups[0], secondaryKey: null, autoExpanded: true }]
          : subGroups
      };
    });

    return {
      groupedData: groupedArray,
    };
  }, [data, pageStore.selected_view, pageStore.year_or_mouth_view, pageStore.columnConfigStaff]);

  const getSortValue = useCallback((item, key, level) => {
    if (level === 'pivot') {
      switch (key) {
        default:
          if (key.startsWith('period-')) {
            const periodKey = key.replace('period-', '');
            if (!item.timeGroups) return 0;

            const periodValue = item.timeGroups[periodKey];
            if (typeof periodValue === 'object' && periodValue !== null) {
              return periodValue.sum || 0;
            }
            return periodValue || 0;
          }
          return 0;
      }
    } else {
      if (key.startsWith('period-')) {
        const periodKey = key.replace('period-', '');
        const date = new Date(item.date);
        let itemPeriodKey;

        if (pageStore.year_or_mouth_view === "month") {
          itemPeriodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        } else {
          itemPeriodKey = `${date.getFullYear()}`;
        }

        return itemPeriodKey === periodKey ? item.Sum : 0;
      } else {
        const value = item[key];
        const column = availableColumns.find(c => c.key === key);

        if (column?.isNumeric) {
          return parseFloat(value) || 0;
        }
        return value || "";
      }
    }
  }, [pageStore.year_or_mouth_view, availableColumns]);

  const sortedGroupedData = useMemo(() => {
    if (!groupedData) return null;

    const compareItems = (a, b, key, level) => {
      const valueA = getSortValue(a, key, level);
      const valueB = getSortValue(b, key, level);

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
      }

      return sortConfig.direction === 'asc'
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA));
    };

    const sortedGroups = [...groupedData].sort((a, b) =>
      compareItems(a, b, sortConfig.key, 'pivot'));

    return sortedGroups.map(group => ({
      ...group,
      subGroups: group.subGroups
        .sort((a, b) => compareItems(a, b, sortConfig.key, 'pivot'))
        .map(subGroup => ({
          ...subGroup,
          items: [...subGroup.items].sort((a, b) =>
            compareItems(a, b, sortConfig.key, 'detail'))
        }))
    }));
  }, [groupedData, sortConfig, getSortValue]);

  const handleExportToXLSX = useCallback(async () => {
    var can = await pageStore.CheckPermission("export excel")
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    const table = document.querySelector("table");
    if (!table) return;

    const workbook = createWorkbookFromTable(table);

    // Сохраняем файл
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "table.xlsx");
  }, [groupedData, allTimeKeys, availableColumns, formatCellValue, formatDateKey]);

  const toggleExpand = useCallback((type, key, subKey = null) => {
    const compositeKey = subKey ? `${key}__${subKey}` : key;
    setExpandedGroups(prev => ({
      ...prev,
      [compositeKey]: !prev[compositeKey]
    }));
  }, []);

  const expandAll = useCallback(() => {
    const newExpanded = {};
    groupedData?.forEach(group => {
      newExpanded[group.primaryKey] = true;
      group.subGroups.forEach(subGroup => {
        newExpanded[`${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`] = true;
      });
    });
    setExpandedGroups(newExpanded);
  }, [groupedData]);

  const collapseAll = useCallback(() => {
    setExpandedGroups({});
  }, []);

  const renderTableHeader = () => (
    <Thead bgColor={"black"} position={"sticky"} top={"0px"} zIndex={11}>
      <Tr>
        <Th color="white" px={0} />
        {availableColumns
          .map(column => (
            <Th
              key={column.key}
              color="white"
              px={4}
              py={2}
              textAlign={column.isNumeric ? "right" : "left"}
              onClick={() => handleSort(column.key, 'detail')}
              cursor="pointer"
            >
              {column.title}
              {sortConfig.key === column.key && sortConfig.level === 'detail' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
            </Th>
          ))}
        {totals.map(t => (
          <Th
            key={t.header}
            color="white"
            px={4}
            py={2}
            textAlign="center"
            onClick={() => handleSort(`period-${t.header}`, 'pivot')}
            cursor="pointer"
          >
            {formatDateKey(t.header)}
            {sortConfig.key === `period-${t.header}` && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        ))}
      </Tr>
    </Thead>
  );

  useEffect(() => {
    const startDate = pageStore.start_date_month
      ? dayjs(pageStore.start_date_month)
      : pageStore.start_date_year
        ? dayjs(pageStore.start_date_year)
        : now.subtract(3, "year");

    const endDate = pageStore.end_date_month
      ? dayjs(pageStore.end_date_month)
      : pageStore.end_date_year
        ? dayjs(pageStore.end_date_year)
        : now;

    const result = calculateTotals(
      data,
      startDate,
      endDate,
      pageStore.year_or_mouth_view
    );

    setTotals(result);
  }, [
    data,
    pageStore.start_date_month,
    pageStore.end_date_month,
    pageStore.start_date_year,
    pageStore.end_date_year,
    pageStore.year_or_mouth_view,
    pageStore.rowHeight
  ]);

  const renderTotalRow = () => (
    <Tr bg="#555">
      <Td px={0} />
      <Td fontWeight="bold" color="white">
        Итого {data.filter(item => item.active && ((item.group != "Отдел продаж") || (item.group == "Отдел продаж" && isManagerRole(item.role)))).length}
      </Td>

      {availableColumns
        .slice(1)
        .map(column => (
          <Th
            key={column.key}
            color="white"
            px={4}
            py={2}
            textAlign={column.isNumeric ? "right" : "left"}
          />
        ))}
      {totals.map(t => (
        <Td key={t.header} color="white" textAlign="center">
          {t.ratio && <b>{t.ratio}</b>}
        </Td>
      ))}
    </Tr>
  );

  const renderGroupRow = (group) => {
    const isExpanded = expandedGroups[group.primaryKey];

    const totalItemsInGroup = group.subGroups.reduce(
      (sum, sg) => sum + sg.items.filter(item => item.active && ((item.group != "Отдел продаж") || (item.group == "Отдел продаж" && isManagerRole(item.role)))).length,
      0
    );

    return (
      <React.Fragment key={group.primaryKey}>
        <Tr bg="#333">
          <Td px={0} py={0}>
            <Button
              size="sm"
              fontSize="20px"
              bg="#333"
              color="white"
              _hover={{ bg: "#222" }}
              onClick={() => toggleExpand('group', group.primaryKey)}
            >
              {isExpanded ? "−" : "+"}
            </Button>
          </Td>
          <Td
            cursor="pointer"
            onClick={() => toggleExpand('group', group.primaryKey)}
            fontWeight="bold"
            color="white"
          >
            {group.primaryKey}
          </Td>
          <Td fontWeight='bold' color='white'>{totalItemsInGroup}</Td>
          {availableColumns
            .slice(2)
            .map(column => (
              <Th
                key={column.key}
                color="white"
                px={4}
                py={2}
                textAlign={column.isNumeric ? "right" : "left"}
              />
            ))}
          {totals.map(t => (<Td key={t.header}></Td>))}
        </Tr>

        {isExpanded && group.subGroups.map(renderSubGroupRow(group))}
      </React.Fragment>
    );
  };

  const renderSubGroupRow = (group) => (subGroup) => {
    const compositeKey = `${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`;
    const isExpanded = expandedGroups[compositeKey] || subGroup.autoExpanded;

    const leaderItems = subGroup.items.filter(
      item => !isManagerRole(item.role)
    );

    const otherItems = subGroup.items.filter(
      item => isManagerRole(item.role)
    );

    const orderedItems = [...leaderItems, ...otherItems];

    return (
      <React.Fragment key={compositeKey}>
        {subGroup.secondaryKey && (
          <Tr bg="#444">
            <Td px={0} py={0}>
              <Button
                color="white"
                fontSize="25px"
                _hover={{ bg: "black", color: "white" }}
                size="sm"
                bg="#444"
                onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
              >
                {isExpanded ? "−" : "+"}
              </Button>
            </Td>
            <Td
              pl={12}
              cursor="pointer"
              onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
              py={2}
              fontWeight="bold"
            >
              {subGroup.secondaryKey}
            </Td>
            <Td fontWeight='bold' color='white'>{subGroup.items.filter(item => item.active && ((item.group != "Отдел продаж") || (item.group == "Отдел продаж" && isManagerRole(item.role)))).length}</Td>
            {availableColumns
              .slice(2)
              .map(column => (
                <Th
                  key={column.key}
                  color="white"
                  px={4}
                  py={2}
                  textAlign={column.isNumeric ? "right" : "left"}
                />
              ))}
            {totals.map(t => (<Td key={t.header}></Td>))}
          </Tr>
        )}

        {isExpanded && orderedItems.map((item, idx) =>
          renderDetailRow(item, idx, orderedItems.length)
        )}
      </React.Fragment>
    );
  };

  const renderDetailRow = (item, index, totalItems) => {
    const isLeader = !isManagerRole(item.role);
    if (!item.active) return;

    return (
      <Tr key={index} bg={index % 2 === 0 ? "#555" : "#666"}>
        <Td px={0} py={0} />
        {availableColumns.map(column => (
          <Td
            key={column.key}
            px={4}
            py={2}
            textAlign={column.isNumeric ? "right" : "left"}
            fontStyle={item.active ? "normal" : "italic"}
            fontWeight={isLeader ? "bold" : "normal"}
          >
            {formatCellValue(item[column.key], column.isNumeric)}
          </Td>
        ))}
        {totals.map(t => (<Td key={t.header}></Td>))}
      </Tr>
    );
  };

  const renderFlatTable = () => (
    <Table tableLayout="fixed" width="100%" minWidth="800px" variant="simple" bg="" color="white">
      <Thead>
        <Tr>
          {availableColumns.map(column => (
            <Th
              key={column.key}
              color="white"
              onClick={() => handleSort(column.key, 'detail')}
              cursor="pointer"
              px={4}
              py={2}
              textAlign={column.isNumeric ? "right" : "left"}
            >
              {column.title}
              {sortConfig.key === column.key && sortConfig.level === 'detail' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {data.map((row, index) => (
          <Tr key={index} bg={index % 2 === 0 ? "#444" : "#333"} height="auto">
            {availableColumns.map(column => (
              <Td
                key={column.key}
                px={4}
                py={pageStore.rowHeight || 2}
                textAlign={column.isNumeric ? "right" : "left"}
                fontStyle={row.active ? "normal" : "italic"}
              >
                {formatCellValue(row[column.key], column.isNumeric)}
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const buttons = (
    <>
      <Button onClick={handleExportToXLSX} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} >
        Экспорт в Excel
      </Button>
      <Button onClick={expandAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }}>
        Развернуть всё
      </Button>
      <Button onClick={collapseAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }}>
        Свернуть всё
      </Button>
    </>
  )


  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity>
        {renderButtons ? renderButtons(buttons) : buttons}
      </Collapse>
      {pageStore?.data_staff.length ?

        <Box w="100%">

          <ColumnFilters />

          <Box maxHeight="95vh" overflowX="auto" borderRadius="md" borderWidth={1} borderColor="gray.300">
            {groupedData ? (
              <Table tableLayout="auto" variant="simple" bg="" color="white" width="auto" maxWidth="fit-content">
                {renderTableHeader()}
                <Tbody>
                  {renderTotalRow()}
                  {sortedGroupedData?.map(renderGroupRow)}
                </Tbody>
              </Table>
            ) : (
              renderFlatTable()
            )}
          </Box>
        </Box>

        : null}
    </>
  );
});

export default TableData;